/**
 * Ratings Hook - React Query Adapter
 *
 * Single source of truth for user ratings using React Query.
 * NO bespoke cache - React Query IS the cache.
 *
 * Features:
 * - useUserRating(): Read a single rating with cache-first SWR
 * - useRateMedia(): Mutation to create/update rating with optimistic update
 * - useDeleteRating(): Mutation to delete rating with optimistic update
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, ApiError } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';

export interface UserRating {
  id: string;
  user_id: string;
  media_id: number;
  media_type: 'movie' | 'tv';
  rating: number;
  rating_source: string;
  created_at: string;
  updated_at: string;
}

interface RatingResponse {
  rating: UserRating | null;
}

interface RateMediaResponse {
  rating: UserRating;
}

// ============================================================================
// useUserRating - Query hook for reading a single rating
// ============================================================================

interface UseUserRatingResult {
  rating: number | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to get user's rating for a specific media item.
 * Uses React Query with cache-first, stale-while-revalidate.
 */
export function useUserRating(
  mediaId: number,
  mediaType: 'movie' | 'tv'
): UseUserRatingResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  const query = useQuery({
    queryKey: queryKeys.rating.one(userId || '', mediaType, mediaId),
    queryFn: async () => {
      if (!token) return null;

      try {
        const data = await apiFetch<RatingResponse>(
          `/ratings/${mediaType}/${mediaId}`,
          { token }
        );
        return data.rating?.rating ?? null;
      } catch (error) {
        // 404 means no rating exists - return null, not error
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    // Only fetch if user is logged in
    enabled: !!userId && !!token && mediaId > 0,
    // Use default staleTime from queryClient (60s)
  });

  return {
    rating: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}

// ============================================================================
// useRateMedia - Mutation hook for creating/updating ratings
// ============================================================================

interface RateMediaVariables {
  mediaId: number;
  mediaType: 'movie' | 'tv';
  rating: number;
  mediaTitle?: string;
  mediaPoster?: string | null;
}

interface UseRateMediaResult {
  rateMedia: (
    mediaId: number,
    mediaType: 'movie' | 'tv',
    rating: number,
    mediaTitle?: string,
    mediaPoster?: string | null
  ) => Promise<UserRating | null>;
  isLoading: boolean;
}

/**
 * Hook to create or update a rating.
 * Uses optimistic updates with rollback on error.
 */
export function useRateMedia(): UseRateMediaResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (variables: RateMediaVariables) => {
      if (!token) throw new Error('Not authenticated');

      const data = await apiFetch<RateMediaResponse>('/ratings', {
        token,
        method: 'POST',
        body: {
          media_id: variables.mediaId,
          media_type: variables.mediaType,
          rating: variables.rating,
          media_title: variables.mediaTitle,
          media_poster: variables.mediaPoster,
        },
      });

      return data.rating;
    },

    onMutate: async (variables) => {
      if (!userId) return;

      const ratingKey = queryKeys.rating.one(userId, variables.mediaType, variables.mediaId);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ratingKey });

      // Snapshot previous value
      const previousRating = queryClient.getQueryData<number | null>(ratingKey);

      // Optimistically update to new value
      queryClient.setQueryData(ratingKey, variables.rating);

      // Return context for rollback
      return { previousRating, ratingKey };
    },

    onError: (_error, _variables, context) => {
      // Rollback to previous value on error
      if (context?.ratingKey) {
        queryClient.setQueryData(context.ratingKey, context.previousRating);
      }
    },

    onSuccess: (data, variables) => {
      if (!userId) return;

      const ratingKey = queryKeys.rating.one(userId, variables.mediaType, variables.mediaId);

      // Ensure cache matches server response
      queryClient.setQueryData(ratingKey, data.rating);
    },
  });

  const rateMedia = async (
    mediaId: number,
    mediaType: 'movie' | 'tv',
    rating: number,
    mediaTitle?: string,
    mediaPoster?: string | null
  ): Promise<UserRating | null> => {
    if (!userId || !token) return null;

    try {
      return await mutation.mutateAsync({
        mediaId,
        mediaType,
        rating,
        mediaTitle,
        mediaPoster,
      });
    } catch (error) {
      console.error('Error saving rating:', error);
      return null;
    }
  };

  return {
    rateMedia,
    isLoading: mutation.isPending,
  };
}

// ============================================================================
// useDeleteRating - Mutation hook for deleting ratings
// ============================================================================

interface DeleteRatingVariables {
  mediaId: number;
  mediaType: 'movie' | 'tv';
}

interface UseDeleteRatingResult {
  deleteRating: (mediaId: number, mediaType: 'movie' | 'tv') => Promise<boolean>;
  isLoading: boolean;
}

/**
 * Hook to delete a rating.
 * Uses optimistic updates with rollback on error.
 */
export function useDeleteRating(): UseDeleteRatingResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (variables: DeleteRatingVariables) => {
      if (!token) throw new Error('Not authenticated');

      await apiFetch(`/ratings/${variables.mediaType}/${variables.mediaId}`, {
        token,
        method: 'DELETE',
      });

      return true;
    },

    onMutate: async (variables) => {
      if (!userId) return;

      const ratingKey = queryKeys.rating.one(userId, variables.mediaType, variables.mediaId);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ratingKey });

      // Snapshot previous value
      const previousRating = queryClient.getQueryData<number | null>(ratingKey);

      // Optimistically set to null
      queryClient.setQueryData(ratingKey, null);

      // Return context for rollback
      return { previousRating, ratingKey };
    },

    onError: (_error, _variables, context) => {
      // Rollback to previous value on error
      if (context?.ratingKey) {
        queryClient.setQueryData(context.ratingKey, context.previousRating);
      }
    },

    onSuccess: (_data, variables) => {
      if (!userId) return;

      const ratingKey = queryKeys.rating.one(userId, variables.mediaType, variables.mediaId);

      // Ensure cache is null (deleted)
      queryClient.setQueryData(ratingKey, null);
    },
  });

  const deleteRating = async (
    mediaId: number,
    mediaType: 'movie' | 'tv'
  ): Promise<boolean> => {
    if (!userId || !token) return false;

    try {
      await mutation.mutateAsync({ mediaId, mediaType });
      return true;
    } catch (error) {
      console.error('Error deleting rating:', error);
      return false;
    }
  };

  return {
    deleteRating,
    isLoading: mutation.isPending,
  };
}

